import { AxiosResponse } from 'axios';
import { api, fetchAllPages } from 'src/common/utils';

import {
  AccountCohort,
  AccountCohortPatchPayload,
  AccountCohortPayload,
  ActiveAccount,
  AddMembersResponse,
  CadenceAudienceConfig,
  CadenceConfigResponse,
  CandidatePatchPayload,
  CohortPreview,
  CohortUserSearchResult,
  CsvImportResult,
  DigestEdition,
  DigestEditionCandidate,
  EditionAudience,
  EditionPatchPayload,
  EditionRecipientsSummary,
  FeatureCatalogEntry,
  FeatureCatalogPatchPayload,
  FeatureCatalogStatus,
  ManualCandidatePayload,
  MediaUploadResponse,
  MemberStatus,
  PaginatedResponse,
  RecipientCohort,
  RecipientCohortCreatePayload,
  RecipientCohortMember,
  RecipientCohortPatchPayload,
  RetryFailedResponse,
} from './types';

const EDITIONS_PATH = '/product_updates/digest_editions';
const CANDIDATES_PATH = '/product_updates/digest_edition_candidates';

export const fetchEditions = (
  _key: string,
  statusFilter?: string,
  audienceFilter?: EditionAudience
): Promise<AxiosResponse<PaginatedResponse<DigestEdition>>> => {
  const params = ['ordering=-created_at'];
  if (statusFilter) params.push(`status=${statusFilter}`);
  if (audienceFilter) params.push(`audience=${audienceFilter}`);
  return fetchAllPages<DigestEdition>(EDITIONS_PATH, params.join('&'));
};

export const createEdition = (payload?: {
  titulo?: string;
  eyebrow_override?: string;
  subheading_override?: string;
  audience?: EditionAudience;
  cohort?: number;
}): Promise<AxiosResponse<DigestEdition>> => api.post(EDITIONS_PATH, { titulo: '', ...payload });

export const archiveEdition = (editionId: string): Promise<AxiosResponse<DigestEdition>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/archive`, {});

export const cancelEdition = (editionId: string): Promise<AxiosResponse<DigestEdition>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/cancel_send`, {});

export const publishBackfill = (
  editionId: string,
  sentAt?: string | null
): Promise<AxiosResponse<DigestEdition>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/publish_backfill`, { sent_at: sentAt ?? null });

export const restoreEdition = (editionId: string): Promise<AxiosResponse<DigestEdition>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/restore`, {});

export const fetchEdition = (
  _key: string,
  editionId: string
): Promise<AxiosResponse<DigestEdition>> => api.get(`${EDITIONS_PATH}/${editionId}`);

export const patchEdition = (
  editionId: string,
  payload: EditionPatchPayload
): Promise<AxiosResponse<DigestEdition>> => api.patch(`${EDITIONS_PATH}/${editionId}`, payload);

export const markEditionReady = (editionId: string): Promise<AxiosResponse<DigestEdition>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/mark_ready_to_send`, {});

export const sendNow = (editionId: string): Promise<AxiosResponse<DigestEdition>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/send_now`, {});

export const retryFailedSends = (editionId: string): Promise<AxiosResponse<RetryFailedResponse>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/retry_failed`, {});

export const sendTestEmail = (
  editionId: string,
  email: string
): Promise<AxiosResponse<{ detail: string }>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/send_test`, { email });

export const fetchAccountSearch = (
  query: string
): Promise<AxiosResponse<PaginatedResponse<{ id: string; name: string }>>> =>
  api.get(`/account_admin?search=${encodeURIComponent(query)}&page_size=10`);

export const fetchCandidates = (
  _key: string,
  editionId: string
): Promise<AxiosResponse<PaginatedResponse<DigestEditionCandidate>>> =>
  fetchAllPages<DigestEditionCandidate>(CANDIDATES_PATH, `edition=${editionId}`);

export const patchCandidate = (
  candidateId: number,
  payload: CandidatePatchPayload
): Promise<AxiosResponse<DigestEditionCandidate>> =>
  api.patch(`${CANDIDATES_PATH}/${candidateId}`, payload);

export const approveCandidate = (
  candidateId: number
): Promise<AxiosResponse<DigestEditionCandidate>> =>
  api.post(`${CANDIDATES_PATH}/${candidateId}/approve`, { support_content_verified: true });

export const rejectCandidate = (
  candidateId: number
): Promise<AxiosResponse<DigestEditionCandidate>> =>
  api.post(`${CANDIDATES_PATH}/${candidateId}/reject`, {});

export const unrejectCandidate = (
  candidateId: number
): Promise<AxiosResponse<DigestEditionCandidate>> =>
  api.post(`${CANDIDATES_PATH}/${candidateId}/unreject`, {});

export const fetchPreviewHtml = (editionId: string): Promise<string> =>
  api
    .get<string>(`${EDITIONS_PATH}/${editionId}/render_preview`, { responseType: 'text' })
    .then((res) => res.data);

export const setEditionAccountCohort = (
  editionId: string,
  accountCohortId: number | null
): Promise<AxiosResponse<DigestEdition>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/account_cohort`, {
    account_cohort_id: accountCohortId,
  });

export const fetchEditionRecipientsSummary = (
  _key: string,
  editionId: string
): Promise<AxiosResponse<EditionRecipientsSummary>> =>
  api.get(`${EDITIONS_PATH}/${editionId}/cohort/recipients_summary`);

export const addManualCandidate = (
  editionId: string,
  payload: ManualCandidatePayload
): Promise<AxiosResponse<DigestEditionCandidate>> =>
  api.post(`${EDITIONS_PATH}/${editionId}/add_candidate`, payload);

export const uploadCandidateMedia = (
  candidateId: number,
  file: File
): Promise<AxiosResponse<MediaUploadResponse>> => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`${CANDIDATES_PATH}/${candidateId}/upload_media`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const FEATURE_CATALOG_PATH = '/product_updates/feature_catalog_entries';

export const fetchFeatureCatalog = (
  query: string,
  excludeEditionId?: string
): Promise<AxiosResponse<PaginatedResponse<FeatureCatalogEntry>>> =>
  api.get(
    `${FEATURE_CATALOG_PATH}?search=${encodeURIComponent(query)}&page_size=20&status=active${
      excludeEditionId ? `&exclude_edition=${excludeEditionId}` : ''
    }`
  );

export type HubFeatureStatus = Extract<FeatureCatalogStatus, 'active' | 'legacy'>;

// Orbit never lists discontinued features. `hub=true` already narrows to active features; Legacy
// ones come through the `status` filter (the hub flag would hide them), so the legacy PR-scan
// entries the hub never publishes are dropped here.
export const fetchHubFeatures = async (
  status: HubFeatureStatus = 'active'
): Promise<AxiosResponse<PaginatedResponse<FeatureCatalogEntry>>> => {
  if (status === 'active') {
    return fetchAllPages<FeatureCatalogEntry>(FEATURE_CATALOG_PATH, 'hub=true&status=active');
  }
  const response = await fetchAllPages<FeatureCatalogEntry>(
    FEATURE_CATALOG_PATH,
    `status=${status}`
  );
  const results = response.data.results.filter((entry) => entry.source !== 'pr_scan');
  return { ...response, data: { ...response.data, count: results.length, results } };
};

export const setFeatureStatus = (
  entryId: number,
  status: FeatureCatalogStatus,
  reason: string
): Promise<AxiosResponse<FeatureCatalogEntry>> =>
  api.post(`${FEATURE_CATALOG_PATH}/${entryId}/set_status`, { status, reason });

export const patchFeatureCatalogEntry = (
  entryId: number,
  payload: Partial<FeatureCatalogPatchPayload>
): Promise<AxiosResponse<FeatureCatalogEntry>> =>
  api.patch(`${FEATURE_CATALOG_PATH}/${entryId}`, payload);

export const addCandidateFromCatalog = (
  editionId: string,
  featureId: number
): Promise<AxiosResponse<DigestEditionCandidate>> =>
  api.post(CANDIDATES_PATH, { edition: Number(editionId), feature: featureId });

export const autoBuildEdition = (
  audience: EditionAudience
): Promise<AxiosResponse<DigestEdition>> => api.post(`${EDITIONS_PATH}/auto_build`, { audience });

const CADENCE_PATH = '/product_updates/digest_cadence_config';

export const fetchCadenceConfig = (_key: string): Promise<AxiosResponse<CadenceConfigResponse>> =>
  api.get(CADENCE_PATH);

export const setCadenceDays = (
  audience: EditionAudience,
  intervalDays: number
): Promise<AxiosResponse<CadenceAudienceConfig>> =>
  api.patch(CADENCE_PATH, { audience, interval_days: intervalDays });

const RECIPIENT_COHORTS_PATH = '/product_updates/recipient_cohorts';

export const fetchRecipientCohorts = (): Promise<
  AxiosResponse<PaginatedResponse<RecipientCohort>>
> => fetchAllPages<RecipientCohort>(RECIPIENT_COHORTS_PATH, 'include_archived=false');

export const createRecipientCohort = (
  payload: RecipientCohortCreatePayload
): Promise<AxiosResponse<RecipientCohort>> => api.post(RECIPIENT_COHORTS_PATH, payload);

export const patchRecipientCohort = (
  cohortId: number,
  payload: RecipientCohortPatchPayload
): Promise<AxiosResponse<RecipientCohort>> =>
  api.patch(`${RECIPIENT_COHORTS_PATH}/${cohortId}`, payload);

export const archiveRecipientCohort = (cohortId: number): Promise<AxiosResponse<RecipientCohort>> =>
  api.post(`${RECIPIENT_COHORTS_PATH}/${cohortId}/archive`, {});

export const fetchRecipientCohortMembers = (
  cohortId: number,
  search: string,
  status: MemberStatus,
  page: number
): Promise<AxiosResponse<{ count: number; results: RecipientCohortMember[] }>> =>
  api.get(
    `${RECIPIENT_COHORTS_PATH}/${cohortId}/members?search=${encodeURIComponent(
      search
    )}&status=${status}&page=${page}`
  );

export const searchCohortUsers = (
  cohortId: number,
  query: string
): Promise<AxiosResponse<CohortUserSearchResult[]>> =>
  api.get(`${RECIPIENT_COHORTS_PATH}/${cohortId}/user_search?q=${encodeURIComponent(query)}`);

export const addCohortMembers = (
  cohortId: number,
  userIds: number[]
): Promise<AxiosResponse<AddMembersResponse>> =>
  api.post(`${RECIPIENT_COHORTS_PATH}/${cohortId}/members`, { user_ids: userIds });

export const removeCohortMember = (
  cohortId: number,
  userId: number
): Promise<AxiosResponse<void>> =>
  api.delete(`${RECIPIENT_COHORTS_PATH}/${cohortId}/members/${userId}`);

export const importCohortMembers = (
  cohortId: number,
  file: File,
  dryRun: boolean
): Promise<AxiosResponse<CsvImportResult>> => {
  const form = new FormData();
  form.append('file', file);
  form.append('dry_run', String(dryRun));
  return api.post(`${RECIPIENT_COHORTS_PATH}/${cohortId}/members/import`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const ACCOUNT_COHORTS_PATH = '/product_updates/account_cohorts';

export const fetchAccountCohorts = (): Promise<AxiosResponse<PaginatedResponse<AccountCohort>>> =>
  fetchAllPages<AccountCohort>(ACCOUNT_COHORTS_PATH);

export const createAccountCohort = (
  payload: AccountCohortPayload
): Promise<AxiosResponse<AccountCohort>> => api.post(ACCOUNT_COHORTS_PATH, payload);

export const patchAccountCohort = (
  cohortId: number,
  payload: AccountCohortPatchPayload
): Promise<AxiosResponse<AccountCohort>> =>
  api.patch(`${ACCOUNT_COHORTS_PATH}/${cohortId}`, payload);

export const archiveAccountCohort = (cohortId: number): Promise<AxiosResponse<AccountCohort>> =>
  api.post(`${ACCOUNT_COHORTS_PATH}/${cohortId}/archive`, {});

export const fetchAccountCohortPreview = (
  _key: string,
  cohortId: number
): Promise<AxiosResponse<CohortPreview>> => api.get(`${ACCOUNT_COHORTS_PATH}/${cohortId}/preview`);

export const fetchAccountCohortActiveAccounts = (): Promise<AxiosResponse<ActiveAccount[]>> =>
  api.get(`${ACCOUNT_COHORTS_PATH}/active_accounts`);
